import { Option, OptionsWithIndustry } from '@/data/types/component';
import { NS_COMMON } from '@/i18n/namespaces';
import { capitalizeWords } from '@/utils/validators';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/react';
import { Ref, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoChevronDown, IoCloseCircle } from 'react-icons/io5';
import { ViewportList } from 'react-viewport-list';
import { twMerge } from 'tailwind-merge';
import { BaseInput } from './BaseInput';
import { TextInput } from './TextInput';

type SelectInputProps<T> = {
  label?: string;
  error?: string;
  name?: string;
  value?: T;
  onChange?: (value: T) => void;
  onReset?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  optional?: boolean;
  itemRef?: Ref<HTMLButtonElement>;
};

export const SelectInput = <T,>({
  label,
  error,
  name,
  value,
  options = [],
  placeholder,
  onChange,
  onReset,
  className,
  disabled,
  required,
  optional,
  itemRef,
}: SelectInputProps<T> & { options: Option<T>[] }) => {
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<string | undefined>(
    options.find(({ value: v }) => v === value)?.label,
  );
  const { t } = useTranslation(NS_COMMON);

  const filtered = useMemo(
    () =>
      filter === ''
        ? options
        : options.filter(({ label }) => {
            return label?.toLowerCase().includes(filter.toLowerCase());
          }),
    [filter, options],
  );

  useEffect(() => {
    setSelected(options.find((v) => v.value === value)?.label);
  }, [options, value]);

  return (
    <BaseInput
      label={capitalizeWords(label ?? '')}
      error={error}
      required={required}
      optional={optional}
    >
      <Listbox
        name={name}
        value={options.find((opt) => opt.value === value)}
        onChange={({ value }) => onChange?.(value)}
      >
        <ListboxButton
          ref={itemRef}
          disabled={disabled}
          className={twMerge(
            'flex flex-row-reverse items-center justify-between gap-2.5 rounded-lg border border-[#CBCBCB] bg-white px-4 py-2',
            'focus:border-[#FFBF00] focus:outline-none focus:ring-0',
            error && 'border-error',
            disabled && 'bg-[#F2F2F2] text-disabled',
            className,
          )}
        >
          {({ value, open }) => (
            <>
              <div className="flex gap-2">
                {onReset && selected && (
                  <IoCloseCircle
                    className="size-6"
                    onClick={() => {
                      onReset();
                    }}
                  />
                )}
                <IoChevronDown
                  className={twMerge('size-6', open && 'rotate-180')}
                />
              </div>

              <span className="truncate">
                {selected || capitalizeWords(placeholder ?? '')}
              </span>
            </>
          )}
        </ListboxButton>

        <ListboxOptions
          className={twMerge(
            '!max-h-72 w-[var(--button-width)] rounded-2xl bg-white shadow-dropdown',
            'focus:outline-none',
          )}
          anchor="bottom"
        >
          {onReset && (
            <div className="m-2">
              <TextInput
                placeholder={t('search')}
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
              />
            </div>
          )}

          {filtered.map((item) => (
            <ListboxOption
              key={item.value as string}
              className={twMerge(
                'flex w-full gap-4 rounded-lg p-4 text-start',
                'data-[selected]:bg-primary data-[selected]:text-white',
                'data-[active][aria-selected=false]:bg-[#F3F3F3] data-[active][aria-selected=false]:text-primary',
              )}
              value={item}
            >
              {item.label}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </BaseInput>
  );
};

type SelectInputWithCategoryProps<T> = SelectInputProps<T> & {
  options: OptionsWithIndustry<T>[];
};

export const SelectInputWithCategory = <T,>({
  label,
  error,
  value,
  options = [],
  placeholder,
  onChange,
  onReset,
  className,
  disabled,
  required,
  optional,
  itemRef,
}: SelectInputWithCategoryProps<T>) => {
  const flattenOptions = useMemo(
    () => options?.map(({ options: ops }) => ops).flat(),
    [options],
  );
  const [selected, setSelected] = useState<string | undefined>(
    flattenOptions.find(({ value: v }) => v === value)?.label,
  );
  const [filter, setFilter] = useState('');
  const { t } = useTranslation(NS_COMMON);

  const filtered = options
    ?.map(({ industry, options: ops }) => ({
      industry,
      options: ops.filter(({ label }) =>
        label.toLowerCase().includes(filter.toLowerCase()),
      ),
    }))
    .filter(({ options }) => options.length > 0);

  useEffect(() => {
    setSelected(flattenOptions.find((v) => v.value === value)?.label);
  }, [options, value]);

  return (
    <BaseInput
      label={capitalizeWords(label ?? '')}
      error={error}
      required={required}
      optional={optional}
    >
      <Menu>
        {({ open }) => (
          <>
            <MenuButton
              ref={itemRef}
              disabled={disabled}
              className={twMerge(
                'flex flex-row-reverse items-center justify-between gap-2.5 rounded-lg border border-[#CBCBCB] bg-white px-4 py-2',
                'focus:border-[#FFBF00] focus:outline-none focus:ring-0',
                error && 'border-error',
                disabled && 'bg-[#F2F2F2] text-disabled',
                className,
              )}
            >
              <div className="flex gap-2">
                {onReset && selected && (
                  <IoCloseCircle
                    className="size-6"
                    onClick={() => {
                      onReset();
                      setSelected('');
                    }}
                  />
                )}
                <IoChevronDown
                  className={twMerge('size-6', open && 'rotate-180')}
                />
              </div>

              <span className="truncate">
                {selected || capitalizeWords(placeholder ?? '')}
              </span>
            </MenuButton>

            <MenuItems
              className={twMerge(
                'h-72 w-[var(--button-width)] rounded-2xl bg-white shadow-dropdown',
                'focus:outline-none',
              )}
              anchor="bottom"
            >
              {onReset && (
                <div className="m-2">
                  <TextInput
                    placeholder={t('search')}
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                  />
                </div>
              )}

              <ViewportList items={filtered}>
                {(item) => (
                  <div key={item.industry} className="border-b-2">
                    <MenuItem
                      as="div"
                      className="text-bold p-2 text-gray-400"
                      disabled
                    >
                      {item.industry}
                    </MenuItem>
                    {item.options.map(({ label, value: val }, index) => (
                      <MenuItem
                        key={index}
                        as="button"
                        className={twMerge(
                          'flex w-full gap-4 rounded-lg p-4 text-start',
                          val === value
                            ? 'bg-primary text-white'
                            : 'hover:bg-[#F3F3F3] data-[focus]:bg-[#F3F3F3]',
                        )}
                        onClick={() => {
                          onChange?.(val);
                          setFilter('');
                          setSelected(label);
                        }}
                      >
                        {label}
                      </MenuItem>
                    ))}
                  </div>
                )}
              </ViewportList>
            </MenuItems>
          </>
        )}
      </Menu>
    </BaseInput>
  );
};
